import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import useAuth from '@contexts/useAuth';
import useSnackbar from '@hooks/useSnackbar';
import { billingCheckout, billingGetPlans, billingActivateFreeTier } from '@api/billing';
import type { BillingPlan } from '@api/billing';
import {
  Container,
  ToggleRow,
  ToggleLabel,
  ToggleSwitch,
  SaveBadge,
  Grid,
  Card,
  PopularBadge,
  PlanName,
  Price,
  PriceAmount,
  PricePeriod,
  BillingPeriod,
  CardTop,
  SectionLabel,
  PlanButton,
  FeatureList,
  Feature,
  PlanSubtitle,
  FreeBadge,
  VolumeSelector,
  LoadingWrapper,
  ErrorText,
} from './PlanPicker.styled';

const ENTERPRISE_FEATURES = [
  'Custom credit volume',
  'SSO & SAML authentication',
  'Custom CAD/CAE/CAM tool integrations',
  'Dedicated account manager',
  'Security audit logs & access controls',
  'SLA & uptime guarantee',
  'Custom AI model fine-tuning',
];

const CONTACT_SALES_EMAIL = 'support@ai-build.com';

function formatPrice(cents: number): string {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars.toLocaleString()}` : `$${dollars.toFixed(2)}`;
}

function parseFeatures(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

interface PlanPickerProps {
  title?: string;
  subtitle?: string;
  /** Whether to show the free tier card. Only shown for personal orgs on the paywall. */
  showFreeTier?: boolean;
  /**
   * Number of free credits granted. Passed from the parent so the UI reflects
   * server config rather than a hardcoded value. Defaults to 0 if undefined.
   */
  freeCredits?: number;
}

const PlanPicker: React.FC<PlanPickerProps> = ({
  title,
  subtitle,
  showFreeTier = false,
  freeCredits = 0,
}) => {
  const intl = useIntl();
  const { user, updateBillingStatus } = useAuth();
  const { showError } = useSnackbar();
  const navigate = useNavigate();

  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [selectedBusinessTierId, setSelectedBusinessTierId] = useState<string>('');

  useEffect(() => {
    if (!user?.token) return;
    billingGetPlans(user.token)
      .then((data) => {
        setPlans(data);
        const firstBusiness = data.find((p) => p.planType === 'BUSINESS');
        if (firstBusiness) {
          setSelectedBusinessTierId(firstBusiness.id);
        }
      })
      .catch((err) => {
        setPlansError(err instanceof Error ? err.message : intl.formatMessage({
          id: 'planPicker.error.loadFailed',
          defaultMessage: 'Failed to load plans',
        }));
      })
      .finally(() => setPlansLoading(false));
  }, [user, intl]);

  const starterPlan = useMemo(() => plans.find((p) => p.planType === 'STARTER'), [plans]);
  const businessPlans = useMemo(
    () => plans.filter((p) => p.planType === 'BUSINESS').sort((a, b) => a.displayOrder - b.displayOrder),
    [plans]
  );
  const selectedBusiness = useMemo(
    () => businessPlans.find((p) => p.id === selectedBusinessTierId),
    [businessPlans, selectedBusinessTierId]
  );

  const handleCheckout = useCallback(
    async (planTierId: string) => {
      if (!user?.token) return;
      setLoading(planTierId);
      try {
        const period = yearly ? 'YEARLY' : 'MONTHLY';
        const { checkoutUrl } = await billingCheckout(user.token, planTierId, period);
        globalThis.location.href = checkoutUrl;
      } catch (err) {
        showError(err instanceof Error ? err.message : intl.formatMessage({
          id: 'planPicker.error.checkoutFailed',
          defaultMessage: 'Failed to start checkout',
        }));
        setLoading(null);
      }
    },
    [user, yearly, showError, intl]
  );

  const handleContactSales = useCallback(() => {
    globalThis.location.href = `mailto:${CONTACT_SALES_EMAIL}?subject=Enterprise%20Plan%20Inquiry`;
  }, []);

  const freeTierMutation = useMutation({
    mutationFn: () => {
      if (!user?.token) return Promise.reject(new Error('Not authenticated'));
      return billingActivateFreeTier(user.token);
    },
    onSuccess: () => {
      updateBillingStatus('ACTIVE');
      navigate('/');
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : intl.formatMessage({
        id: 'planPicker.error.freeTierFailed',
        defaultMessage: 'Failed to activate free tier',
      }));
    },
  });

  // Disable all plan CTAs while any billing action is in flight to prevent concurrent requests.
  const billingActionRunning = loading !== null || freeTierMutation.isPending;

  const billingLabel = yearly
    ? intl.formatMessage({ id: 'planPicker.billedYearly', defaultMessage: 'Billed yearly' })
    : intl.formatMessage({ id: 'planPicker.billedMonthly', defaultMessage: 'Billed monthly' });

  const displayTitle = title ?? intl.formatMessage({
    id: 'planPicker.title',
    defaultMessage: 'Choose the right plan for your team',
  });

  const displaySubtitle = subtitle ?? intl.formatMessage({
    id: 'planPicker.subtitle',
    defaultMessage: 'Pay only for what you use. Scale with credits as your engineering projects grow.',
  });

  if (plansLoading) {
    return (
      <Container>
        <LoadingWrapper>
          {intl.formatMessage({ id: 'planPicker.loading', defaultMessage: 'Loading plans...' })}
        </LoadingWrapper>
      </Container>
    );
  }

  return (
    <Container>
      <h2>{displayTitle}</h2>
      <p>{displaySubtitle}</p>

      <ToggleRow>
        <ToggleLabel $active={!yearly}>
          {intl.formatMessage({ id: 'planPicker.monthly', defaultMessage: 'Monthly' })}
        </ToggleLabel>
        <ToggleSwitch $yearly={yearly} onClick={() => setYearly(!yearly)} />
        <ToggleLabel $active={yearly}>
          {intl.formatMessage({ id: 'planPicker.yearly', defaultMessage: 'Yearly' })}
        </ToggleLabel>
        <SaveBadge $visible={yearly}>
          {intl.formatMessage({ id: 'planPicker.save', defaultMessage: 'Save 16%' })}
        </SaveBadge>
      </ToggleRow>

      {plansError && <ErrorText>{plansError}</ErrorText>}

      <Grid>
        {showFreeTier && (
          <Card>
            <FreeBadge>
              {intl.formatMessage({ id: 'planPicker.freeBadge', defaultMessage: 'No credit card' })}
            </FreeBadge>
            <CardTop>
              <PlanName>Free</PlanName>
              <Price>
                <PriceAmount>$0</PriceAmount>
                <PricePeriod>/ month</PricePeriod>
              </Price>
              <BillingPeriod>
                {intl.formatMessage(
                  { id: 'planPicker.freeSubtitle', defaultMessage: '{count} credits to get started' },
                  { count: freeCredits }
                )}
              </BillingPeriod>
            </CardTop>

            <PlanButton
              $loading={freeTierMutation.isPending}
              disabled={billingActionRunning}
              onClick={() => freeTierMutation.mutate()}
            >
              {freeTierMutation.isPending
                ? intl.formatMessage({ id: 'planPicker.activating', defaultMessage: 'Activating...' })
                : intl.formatMessage({ id: 'planPicker.startForFree', defaultMessage: 'Start for Free' })}
            </PlanButton>

            <FeatureList>
              <Feature>
                {intl.formatMessage(
                  { id: 'planPicker.free.feature1', defaultMessage: '{count} one-time credits' },
                  { count: freeCredits }
                )}
              </Feature>
              <Feature>{intl.formatMessage({ id: 'planPicker.free.feature2', defaultMessage: 'No credit card required' })}</Feature>
              <Feature>{intl.formatMessage({ id: 'planPicker.free.feature3', defaultMessage: 'Full platform access' })}</Feature>
            </FeatureList>
          </Card>
        )}

        {starterPlan && (
          <Card>
            <CardTop>
              <PlanName>Starter</PlanName>
              <Price>
                <PriceAmount>
                  {formatPrice(yearly ? starterPlan.yearlyPriceCents / 12 : starterPlan.monthlyPriceCents)}
                </PriceAmount>
                <PricePeriod>/ month</PricePeriod>
              </Price>
              <BillingPeriod>{billingLabel}</BillingPeriod>
            </CardTop>

            <PlanButton
              $loading={loading === starterPlan.id}
              disabled={billingActionRunning}
              onClick={() => handleCheckout(starterPlan.id)}
            >
              {loading === starterPlan.id
                ? intl.formatMessage({ id: 'planPicker.redirecting', defaultMessage: 'Redirecting...' })
                : intl.formatMessage({ id: 'planPicker.getStarted', defaultMessage: 'Get Started' })}
            </PlanButton>

            <FeatureList>
              {parseFeatures(starterPlan.features).map((f) => (
                <Feature key={f}>{f}</Feature>
              ))}
            </FeatureList>
          </Card>
        )}

        {businessPlans.length > 0 && selectedBusiness && (
          <Card $highlighted>
            <PopularBadge>
              {intl.formatMessage({ id: 'planPicker.mostPopular', defaultMessage: 'Most Popular' })}
            </PopularBadge>
            <CardTop>
              <PlanName>Business</PlanName>
              <Price>
                <PriceAmount>
                  {formatPrice(
                    yearly
                      ? selectedBusiness.yearlyPriceCents / 12
                      : selectedBusiness.monthlyPriceCents
                  )}
                </PriceAmount>
                <PricePeriod>/ month</PricePeriod>
              </Price>
              <BillingPeriod>{billingLabel}</BillingPeriod>

              <SectionLabel>
                {intl.formatMessage({ id: 'planPicker.creditsPerMonth', defaultMessage: 'Credits per month' })}
              </SectionLabel>
              <VolumeSelector
                value={selectedBusinessTierId}
                onChange={(e) => setSelectedBusinessTierId(e.target.value)}
              >
                {businessPlans.map((bp) => (
                  <option key={bp.id} value={bp.id}>
                    {bp.credits.toLocaleString()} credits
                  </option>
                ))}
              </VolumeSelector>
            </CardTop>

            <PlanButton
              $primary
              $loading={loading === selectedBusiness.id}
              disabled={billingActionRunning}
              onClick={() => handleCheckout(selectedBusiness.id)}
            >
              {loading === selectedBusiness.id
                ? intl.formatMessage({ id: 'planPicker.redirecting', defaultMessage: 'Redirecting...' })
                : intl.formatMessage({ id: 'planPicker.getStarted', defaultMessage: 'Get Started' })}
            </PlanButton>

            <SectionLabel>
              {intl.formatMessage({ id: 'planPicker.everythingInStarter', defaultMessage: 'Everything in Starter, plus' })}
            </SectionLabel>
            <FeatureList>
              {parseFeatures(selectedBusiness.features).map((f) => (
                <Feature key={f}>{f}</Feature>
              ))}
            </FeatureList>
          </Card>
        )}

        <Card>
          <CardTop>
            <PlanName>Enterprise</PlanName>
            <Price>
              <PriceAmount>Custom</PriceAmount>
            </Price>
            <PlanSubtitle>
              {intl.formatMessage({ id: 'planPicker.enterpriseSubtitle', defaultMessage: 'For large teams & orgs' })}
            </PlanSubtitle>
          </CardTop>

          <PlanButton onClick={handleContactSales}>
            {intl.formatMessage({ id: 'planPicker.contactSales', defaultMessage: 'Contact Sales' })}
          </PlanButton>

          <SectionLabel>
            {intl.formatMessage({ id: 'planPicker.everythingInBusiness', defaultMessage: 'Everything in Business, plus' })}
          </SectionLabel>
          <FeatureList>
            {ENTERPRISE_FEATURES.map((f) => (
              <Feature key={f}>{f}</Feature>
            ))}
          </FeatureList>
        </Card>
      </Grid>
    </Container>
  );
};

export default PlanPicker;
